import type { TreeNodeType, TreeOperationsReturn } from '@/types/composables/TreeOperationsTypes';

export function useTreeOperations<T extends TreeNodeType & { children?: T[] }>(): TreeOperationsReturn<T> {
    const removeNodeFromTree = (nodes: T[], nodeId: number, captureRemovedNode?: { value: T | null }): T[] => {
        return nodes.filter(node => {
            if (node.id === nodeId) {
                if (captureRemovedNode) {
                    captureRemovedNode.value = { ...node };
                }

                return false;
            }
            if (node.children && node.children.length > 0) {
                node.children = removeNodeFromTree(node.children, nodeId, captureRemovedNode);
            }

            return true;
        });
    };

    const addNodeToTree = (nodes: T[], parentId: number, newNode: T): boolean => {
        for (const node of nodes) {
            if (node.id === parentId) {
                newNode.level = node.level + 1;
                if (!node.children) {
                    node.children = [];
                }
                node.children.push(newNode);

                return true;
            }
            if (node.children && addNodeToTree(node.children, parentId, newNode)) {
                return true;
            }
        }

        return false;
    };

    const moveNodeInTree = (nodes: T[], nodeId: number, direction: 'up' | 'down'): boolean => {
        for (let i = 0; i < nodes.length; i++) {
            const node = nodes[i];

            if (node.id === nodeId) {
                const targetIndex = direction === 'up' ? i - 1 : i + 1;
                if (targetIndex >= 0 && targetIndex < nodes.length) {
                    [nodes[i], nodes[targetIndex]] = [nodes[targetIndex], nodes[i]];

                    return true;
                }

                return false;
            }

            if (node.children && moveNodeInTree(node.children, nodeId, direction)) {
                return true;
            }
        }

        return false;
    };

    const buildFilteredTree = (nodes: T[], nodeIdsToInclude: Set<number>): T[] => {
        return nodes
            .filter(node => nodeIdsToInclude.has(node.id))
            .map(node => {
                const filteredNode = { ...node };
                if (filteredNode.children && filteredNode.children.length > 0) {
                    filteredNode.children = buildFilteredTree(filteredNode.children, nodeIdsToInclude);
                }

                return filteredNode;
            });
    };

    const flattenTree = (nodes: T[]): T[] => {
        const result: T[] = [];

        const traverse = (nodeList: T[]) => {
            for (const node of nodeList) {
                result.push(node);
                if (node.children && node.children.length > 0) {
                    traverse(node.children);
                }
            }
        };

        traverse(nodes);

        return result;
    };

    const moveSiblingUp = (nodes: T[], nodeId: number): boolean => {
        return moveNodeInTree(nodes, nodeId, 'up');
    };

    const moveSiblingDown = (nodes: T[], nodeId: number): boolean => {
        return moveNodeInTree(nodes, nodeId, 'down');
    };

    const findNodeById = (nodes: T[], nodeId: number): T | undefined => {
        for (const node of nodes) {
            if (node.id === nodeId) {
                return node;
            }
            if (node.children) {
                const found = findNodeById(node.children, nodeId);
                if (found) {
                    return found;
                }
            }
        }

        return undefined;
    };

    const getNodePath = (nodes: T[], nodeId: number): T[] => {
        const path: T[] = [];
        const findPath = (nodeList: T[], currentPath: T[]): boolean => {
            for (const node of nodeList) {
                const newPath = [...currentPath, node];
                if (node.id === nodeId) {
                    path.push(...newPath);

                    return true;
                }
                if (node.children && findPath(node.children, newPath)) {
                    return true;
                }
            }

            return false;
        };

        findPath(nodes, []);

        return path;
    };

    const updateChildrenLevels = (node: T, levelDelta: number): void => {
        if (node.children && node.children.length > 0) {
            for (const child of node.children) {
                child.level += levelDelta;
                updateChildrenLevels(child, levelDelta);
            }
        }
    };

    const generateNodePath = (nodeName: string, parentPath?: string): string => {
        const sanitizedName = nodeName
            .toLowerCase()
            .replace(/\./g, '-')
            .replace(/[^a-z0-9-_]/g, '_');

        return parentPath ? `${parentPath}.${sanitizedName}` : sanitizedName;
    };

    const updateNodePaths = (node: T, newPath: string): void => {
        if ('path' in node) {
            node.path = newPath;
        }

        if (node.children && node.children.length > 0) {
            for (const child of node.children) {
                if ('name' in child && 'path' in child) {
                    const childName = child.name as string;
                    const childPath = generateNodePath(childName, newPath);
                    updateNodePaths(child, childPath);
                }
            }
        }
    };

    const getNodePosition = (nodes: T[], parentId: number | null, targetNodeId: number): number => {
        if (parentId === null) {
            return nodes.findIndex(node => node.id === targetNodeId);
        }

        const parent = findNodeById(nodes, parentId);
        if (!parent?.children) {
            return -1;
        }

        return parent.children.findIndex(child => child.id === targetNodeId);
    };

    const getSiblings = (flatItems: T[], node: T, options: { includeRealNodesOnly?: boolean } = {}): T[] => {
        return flatItems.filter(n => {
            if (n.parent_id !== node.parent_id || n.level !== node.level || n.id === node.id) {
                return false;
            }

            if (options.includeRealNodesOnly && n.id <= 0) {
                return false;
            }

            return true;
        });
    };

    return {
        removeNodeFromTree,
        addNodeToTree,
        moveNodeInTree,
        buildFilteredTree,
        flattenTree,

        findNodeById,
        getNodePath,
        updateChildrenLevels,

        moveSiblingUp,
        moveSiblingDown,

        generateNodePath,
        updateNodePaths,
        getNodePosition,
        getSiblings,
    };
}
